import useEditorStore from '../../shared/stores/useEditorStore';
import useWorkflowStore from '../../shared/stores/useWorkflowStore';
import { GLOBAL_STEP_ORDER } from '../../shared/constants/flowConfig';
import { sceneHasLoadedMesh } from '../../shared/lib/scanMeshLoadGate';
import { clearCompareBaseline } from '../../shared/lib/compareBaselineSnapshot';
import { isCheckpointStep, isOnEnterCaptureStep } from './constants';
import {
  captureMeshForStep,
  clearMeshForStep,
  clearMeshPool,
  hasMeshForStep,
} from './meshSnapshot';
import { captureSessionState, restoreSessionState } from './sessionSnapshot';
import {
  persistStepSceneToCache,
  hydrateUndoSnapshotsForStep,
  discardStepScenesFromCache,
  hydrateStepMeshFromCache,
} from './sceneCacheBridge';
import { resetAppliedToolsForSteps } from '../../shared/lib/stepToolReset';
import {
  clearAllUndoSnapshots,
  clearUndoSnapshotsForSteps,
} from './undoSnapshot';
import useStepCheckpointStore from './stepCheckpointStore';
import {
  invalidateUndoAfterStep,
  invalidateUndoForStep,
} from './undo/undoInvalidation';
import { undoAppliesAfterStep } from './undo/stepRevert';
import {
  replayToStep,
  canReplayToStep,
  useOperationLogStore,
  captureBaseMesh,
  hasBaseMesh,
  clearBaseMesh,
} from './operationLog';

const LOG_PREFIX = '[StepCheckpoint]';

/**
 * Ceiling on the undo-router pass in a revert. Its sources can reach the network
 * (a stored pre-apply blob, a previous-step checkpoint), and a request that never
 * settles would hold the "Reverting changes…" overlay open forever with no way
 * out. On expiry the revert drops to the local mesh snapshot instead.
 */
const REVERT_UNDO_TIMEOUT_MS = 25000;

/** First checkpoint step that produces an editable socket — the replay origin. */
const FIRST_CHECKPOINT_STEP = GLOBAL_STEP_ORDER.find(isCheckpointStep);
const SCENE_WAIT_MS = 8000;
const SCENE_POLL_MS = 16;

/** Set before navigate() so handleStepTransition skips capture and re-restore. */
let pendingRevertTargetStepId = null;

export function setPendingRevertNavigation(targetStepId) {
  pendingRevertTargetStepId = targetStepId;
}

function clearCheckpointRecord(stepId) {
  useStepCheckpointStore.getState().removeCheckpoint(stepId);
}

function resetStepCompletionAfter(stepId) {
  const idx = GLOBAL_STEP_ORDER.indexOf(stepId);
  if (idx < 0) return;
  const { actions } = useWorkflowStore.getState();
  for (const step of GLOBAL_STEP_ORDER.slice(idx + 1)) {
    actions.setStepCompletion(step, false);
  }
}

function discardCheckpointsAfter(stepId) {
  const idx = GLOBAL_STEP_ORDER.indexOf(stepId);
  if (idx < 0) return;
  const laterSteps = GLOBAL_STEP_ORDER.slice(idx + 1);
  for (const step of laterSteps) {
    clearMeshForStep(step);
    clearCheckpointRecord(step);
  }
  // The work is gone from the socket, so the cards must stop claiming it. Done
  // explicitly rather than left to the target step's session snapshot: a step
  // rebuilt by the replay engine has no session record, and the snapshot does not
  // reach every tool store. A card left saying "Applied" here is also a dead Undo
  // button, because the lines below delete everything that undo could restore.
  resetAppliedToolsForSteps(laterSteps);
  // Those tools are un-applied again, so their pre-apply snapshots are stale —
  // leaving them would let Undo rewind the socket past the revert target.
  clearUndoSnapshotsForSteps(laterSteps);
  // The persistent scene cache holds the same three things per step (mesh, tool
  // session, pre-apply snapshots) and outlives the pools above, so it has to be
  // discarded too — otherwise re-entering the step reinstates the discarded work.
  void discardStepScenesFromCache(laterSteps);
  // Drop the operation log for everything after the target so replay reconstructs
  // only the work up to this step.
  useOperationLogStore.getState().clearOperationsAfter(stepId);
  // Same reasoning, but for the durable material: a stored pre-apply mesh that
  // outlives its discarded step would let a *different device* rewind the socket
  // past this revert. Fire-and-forget — hydration is gated on completion state, so
  // a failed delete cannot resurrect the work, it only wastes storage.
  void invalidateUndoAfterStep(stepId);
}

/**
 * Snapshot the editable socket as the replay base the first time we reach the
 * earliest checkpoint step, so later steps can be rebuilt from operations once
 * their snapshots are dropped.
 */
function maybeCaptureBaseMesh(stepId) {
  if (stepId !== FIRST_CHECKPOINT_STEP || hasBaseMesh()) return;
  const mesh = useEditorStore.getState().sceneRefs?.shadedMesh;
  if (mesh && captureBaseMesh(mesh)) {
    console.log(`${LOG_PREFIX} captured replay base mesh at ${stepId}`);
  }
}

function waitForSceneReady() {
  return new Promise((resolve) => {
    const started = performance.now();
    const tick = () => {
      const refs = useEditorStore.getState().sceneRefs;
      if (sceneHasLoadedMesh(refs)) {
        resolve(true);
        return;
      }
      if (performance.now() - started > SCENE_WAIT_MS) {
        resolve(false);
        return;
      }
      setTimeout(tick, SCENE_POLL_MS);
    };
    tick();
  });
}

export function hasStepCheckpoint(stepId) {
  return isCheckpointStep(stepId) && useStepCheckpointStore.getState().hasCheckpoint(stepId);
}

/**
 * Persist mesh + tool/editor session for a workflow step.
 * @param {string} stepId
 */
export function captureStepCheckpoint(stepId) {
  if (!isCheckpointStep(stepId)) return false;

  const hasMesh = captureMeshForStep(stepId);
  if (!hasMesh) {
    console.warn(`${LOG_PREFIX} skip capture — no mesh for step ${stepId}`);
    return false;
  }

  useStepCheckpointStore.getState().setCheckpoint(stepId, {
    hasMesh: true,
    session: captureSessionState(),
  });

  // Mirror to the persistent client cache so this step survives a reload.
  persistStepSceneToCache(stepId).catch((e) =>
    console.warn(`${LOG_PREFIX} cache persist failed for ${stepId}`, e),
  );

  console.log(`${LOG_PREFIX} captured ${stepId}`);
  return true;
}

/**
 * Reinstate the tool/editor session for a step, without touching geometry.
 *
 * Split out of {@link restoreStepCheckpoint} because a revert that already put
 * the socket back through the undo router needs this half and only this half —
 * re-installing the mesh pool entry on top would undo the undo.
 *
 * @param {string} stepId
 */
async function restoreStepSession(stepId) {
  const record = useStepCheckpointStore.getState().getCheckpoint(stepId);
  if (record?.session) {
    restoreSessionState(record.session);
  }

  // restoreSessionState just nulled this step's live undo commands, so make sure
  // its tools have the durable pre-apply snapshots to fall back on. No-op when
  // they are already in memory (same-session navigation).
  await hydrateUndoSnapshotsForStep(stepId);

  const editor = useEditorStore.getState();
  editor.applyAlignmentSocketPose?.();
  editor.bumpSocketTransformRevision?.();
}

/**
 * Restore mesh + session for a workflow step.
 * @param {string} stepId
 */
export async function restoreStepCheckpoint(stepId) {
  if (!isCheckpointStep(stepId)) return false;
  // Reconstructable when there's an in-session snapshot/mesh OR the replay engine
  // can rebuild it (base mesh / server anchor) — the latter covers fresh-load jumps.
  if (!hasStepCheckpoint(stepId) && !hasMeshForStep(stepId) && !canReplayToStep(stepId)) {
    return false;
  }

  const store = useStepCheckpointStore.getState();
  store.setIsRestoring(true);

  try {
    const ready = await waitForSceneReady();
    if (!ready) {
      console.warn(`${LOG_PREFIX} scene not ready — restore aborted for ${stepId}`);
      return false;
    }

    // Reconstruct the model *as it was at this step* — anchor on the nearest
    // snapshot and replay the operation log forward, with a progressive
    // full-screen loader. Falls back to the exact snapshot when present.
    const meshOk = await replayToStep(stepId, { showLoader: true });
    if (!meshOk) {
      console.warn(`${LOG_PREFIX} mesh restore failed for ${stepId}`);
      return false;
    }

    await restoreStepSession(stepId);

    console.log(`${LOG_PREFIX} restored ${stepId}`);
    return true;
  } finally {
    useStepCheckpointStore.getState().setIsRestoring(false);
  }
}

export function clearAllStepCheckpoints() {
  clearMeshPool();
  clearBaseMesh();
  clearAllUndoSnapshots();
  // Otherwise the previous design's original would be reinstated as the compare
  // baseline when the next one resumes straight onto a checkpoint step.
  clearCompareBaseline();
  useOperationLogStore.getState().clearAll();
  useStepCheckpointStore.getState().clearAll();
  console.log(`${LOG_PREFIX} cleared all checkpoints`);
}

/**
 * {@link undoAppliesAfterStep} under a wall-clock ceiling. Resolves to the same
 * shape on expiry so the caller just takes the mesh-snapshot branch.
 *
 * @param {string} targetStepId
 */
async function runUndoPassWithTimeout(targetStepId) {
  const controller = new AbortController();
  let timer = null;
  const expiry = new Promise((resolve) => {
    timer = setTimeout(() => {
      controller.abort();
      console.warn(
        `${LOG_PREFIX} undo pass for ${targetStepId} exceeded ${REVERT_UNDO_TIMEOUT_MS}ms — ` +
          'abandoning it for the step mesh snapshot',
      );
      resolve({ ok: false, reason: 'timed_out' });
    }, REVERT_UNDO_TIMEOUT_MS);
  });

  try {
    return await Promise.race([
      undoAppliesAfterStep(targetStepId, { signal: controller.signal }),
      expiry,
    ]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Roll the design back to how `targetStepId` left it, discarding everything
 * applied after it.
 *
 * The socket itself is put back the way a tool card's Undo button does it —
 * through the undo router, off the pre-apply material recorded at apply time. See
 * `undo/stepRevert.js` for why that is the primary path and the step mesh pool is
 * only the fallback.
 *
 * @param {string} targetStepId
 */
export async function revertToStep(targetStepId) {
  if (!isCheckpointStep(targetStepId)) {
    console.warn(`${LOG_PREFIX} revert skipped — not a checkpoint step: ${targetStepId}`);
    return false;
  }

  // Completion first: `discardCheckpointsAfter` republishes the cached resume
  // metadata, and that has to carry the *post*-revert completion flags.
  resetStepCompletionAfter(targetStepId);

  // Held across the whole rewind so the debounced scene autosave cannot capture a
  // half-reverted socket as the current step's saved state.
  useStepCheckpointStore.getState().setIsRestoring(true);
  let undone;
  try {
    // Restoring a pre-apply socket swaps the shaded mesh, so the scene has to
    // hold one first — otherwise every source fails and the router falls all the
    // way through to a needless checkpoint download.
    await waitForSceneReady();

    // Reach the durable copy before deciding anything. After a reload only the
    // resumed step has in-memory pools, so without this both the undo pass and
    // the mesh fallback below start empty and the revert does nothing at all.
    await hydrateStepMeshFromCache(targetStepId);

    // Before `discardCheckpointsAfter`, which deletes the snapshots, manifests
    // and blobs this reads.
    undone = await runUndoPassWithTimeout(targetStepId);

    discardCheckpointsAfter(targetStepId);

    if (undone.ok) {
      // Socket already back; only the target step's tool session is still missing.
      await restoreStepSession(targetStepId);
      console.log(
        `${LOG_PREFIX} reverted to ${targetStepId} via ${undone.source} (${undone.from})`,
      );
      return true;
    }
  } finally {
    useStepCheckpointStore.getState().setIsRestoring(false);
  }

  // "Nothing applied" is the ordinary case for stepping back off a step the user
  // only looked at; anything else means undo material was expected and missing.
  const fallbackNote =
    `${LOG_PREFIX} nothing to undo after ${targetStepId} (${undone.reason}) — ` +
    'using the step mesh snapshot';
  if (undone.reason === 'nothing_applied') console.log(fallbackNote);
  else console.warn(fallbackNote);

  if (hasStepCheckpoint(targetStepId) || hasMeshForStep(targetStepId) || canReplayToStep(targetStepId)) {
    // Propagated, not swallowed: reporting a revert that did not happen is what
    // let the caller navigate away with the discarded geometry still on screen.
    const restored = await restoreStepCheckpoint(targetStepId);
    console.log(`${LOG_PREFIX} ${restored ? 'reverted to' : 'FAILED to revert to'} ${targetStepId}`);
    return restored;
  }

  console.warn(`${LOG_PREFIX} revert to ${targetStepId} — no snapshot to restore`);
  return false;
}

/**
 * @param {string | null} previousStepId
 * @param {string | null} nextStepId
 */
export async function handleStepTransition(previousStepId, nextStepId) {
  const isRevertNav =
    pendingRevertTargetStepId != null && pendingRevertTargetStepId === nextStepId;

  if (previousStepId && previousStepId !== nextStepId) {
    if (isRevertNav) {
      pendingRevertTargetStepId = null;
      if (isCheckpointStep(previousStepId)) {
        clearMeshForStep(previousStepId);
        clearCheckpointRecord(previousStepId);
        useOperationLogStore.getState().clearOperationsForStep(previousStepId);
        resetAppliedToolsForSteps([previousStepId]);
        clearUndoSnapshotsForSteps([previousStepId]);
        void discardStepScenesFromCache([previousStepId]);
        void invalidateUndoForStep(previousStepId);
      }
    } else if (isCheckpointStep(previousStepId)) {
      captureStepCheckpoint(previousStepId);
    }
  }

  const store = useStepCheckpointStore.getState();
  store.setActiveStepId(nextStepId);

  if (nextStepId) maybeCaptureBaseMesh(nextStepId);

  if (isRevertNav) {
    return;
  }

  if (nextStepId && hasStepCheckpoint(nextStepId)) {
    await restoreStepCheckpoint(nextStepId);
    return;
  }

  if (nextStepId && isOnEnterCaptureStep(nextStepId)) {
    const ready = await waitForSceneReady();
    if (ready) {
      captureStepCheckpoint(nextStepId);
    }
  }
}
